import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';

const LoginPage = () => {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(username, password);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D] p-4">
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1447933601403-0c6688de566e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxjb2ZmZWUlMjBiZWFucyUyMGRhcmt8ZW58MHx8fHwxNzE5MzAwMDAwfDA&ixlib=rb-4.1.0&q=85')`
          }}
        />
      </div>

      <Card className="w-full max-w-md relative z-10 shadow-2xl border-[#333] bg-[#1A1A1A]" data-testid="login-card">
        <CardHeader className="space-y-4 text-center">
          <div className="mx-auto w-20 h-20 flex items-center justify-center">
            <img src="/brothers-logo.png" alt="Brothers Coffee" className="w-full h-full object-contain" />
          </div>
          <div>
            <CardTitle className="text-2xl font-semibold text-white">Shift Recon</CardTitle>
            <CardDescription className="text-gray-400 mt-1">
              Brothers Coffee Roastery
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive" data-testid="login-error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-medium text-gray-300">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                required
                disabled={loading}
                className="h-11 bg-[#0D0D0D] border-[#333] text-white placeholder:text-gray-500 focus:border-[#D97B22] focus:ring-[#D97B22]"
                data-testid="login-username"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-gray-300">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={loading}
                className="h-11 bg-[#0D0D0D] border-[#333] text-white placeholder:text-gray-500 focus:border-[#D97B22] focus:ring-[#D97B22]"
                data-testid="login-password"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-[#D97B22] hover:bg-[#B86A1D] text-white font-medium"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
